import React, { useEffect, useState, useCallback } from "react";
import api from "../axiosSetup";
import cfg from "../../config";
import AdminFormVorschau from "./AdminFormVorschau";
import AdminCostumeTypeDefinition from "./AdminCostumeTypeDefinition";
import {
    FELDTYPEN, typBezeichnung, brauchtOptionen, technischerName,
    optionenZuText, textZuOptionen,
} from "./feldtypen";

/**
 * Die allgemeinen Felder eines Kostuem-Formulars - die, die fuer jeden Typ
 * gelten. Welches Formular gemeint ist, gibt die Formularverwaltung vor;
 * ausgewaehlt wird es dort in derselben Liste wie die uebrigen Formulare.
 *
 * Drei Feldarten lassen sich nicht bearbeiten, nur verschieben: die
 * Typauswahl, der Platzhalter fuer die typabhaengigen Felder und der
 * Abschnitt mit den bestellbaren Teilen. Der Server fuellt sie beim
 * Ausliefern - hier stuenden sie sonst ein zweites Mal.
 */

/** Die Stellen, an denen der Server die Felder eines Typs einsetzt. */
const istTypStelle = (f) =>
    f.festgelegt && (f.type === "type-questions" || f.type === "section");

const AdminCostumeForms = ({ component, typen }) => {
    const [gewaehlterTyp, setGewaehlterTyp] = useState("");
    const [formular, setFormular]   = useState(null);
    const [felder, setFelder]       = useState([]);
    const [meldung, setMeldung]     = useState(null);
    const [speichert, setSpeichert] = useState(false);
    const [zeigtVorschau, setZeigtVorschau] = useState(false);
    // Der zuletzt geladene Stand, um Ungespeichertes zu erkennen.
    const [urstand, setUrstand]     = useState(null);

    const kopf = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } });

    const lade = useCallback(async () => {
        try {
            const { data } = await api.get("/api/costume-forms", kopf());
            const gefunden = (data.formulare || []).find(f => f.component === component);
            setFormular(gefunden || null);
            const geladene = (gefunden?.felder || []).map(f => ({
                ...f, optionenText: optionenZuText(f.options),
            }));
            setFelder(geladene);
            setUrstand(JSON.stringify(geladene));
            setMeldung(gefunden ? null : {
                art: "danger", text: "Dieses Formular gibt es nicht (mehr).",
            });
            return !!gefunden;
        } catch {
            setMeldung({ art: "danger", text: "Das Formular konnte nicht geladen werden." });
            return false;
        }
    }, [component]);

    useEffect(() => { lade(); }, [lade]);

    // Die Vorschau zeigt den Bearbeitungsstand, das echte Formular den
    // gespeicherten. Ohne diesen Unterschied sichtbar zu machen, wirkt es,
    // als kaeme die Aenderung nicht an.
    const nichtGespeichert = urstand !== null && JSON.stringify(felder) !== urstand;

    // Drei Stellen fuellt erst der Server, wenn er das Formular ausliefert:
    // die Typenliste in der Auswahl, die Fragen des Typs hinter dem
    // Platzhalter und die bestellbaren Teile im Abschnitt. Ohne sie zeigte
    // die Vorschau dort nur Hinweise - die Typauswahl war gesperrt, und
    // keine einzige Bedingung liess sich ausprobieren.
    const [ausgeliefert, setAusgeliefert] = useState(null);

    useEffect(() => { setAusgeliefert(null); }, [component]);

    useEffect(() => {
        if (!zeigtVorschau || ausgeliefert) return;
        let verworfen = false;
        api.get(`/api/form/${component}`, kopf())
            .then(({ data }) => { if (!verworfen) setAusgeliefert(data || { fields: [] }); })
            // Die Vorschau soll auch dann aufgehen, wenn das misslingt - dann
            // eben wieder mit den Hinweisen statt mit echten Typen.
            .catch(() => { if (!verworfen) setAusgeliefert({ fields: [] }); });
        return () => { verworfen = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zeigtVorschau, ausgeliefert, component]);

    /** Die bearbeiteten Felder, ergaenzt um das, was der Server beisteuert. */
    const vorschauFelder = () => {
        const vomServer = Array.isArray(ausgeliefert?.fields) ? ausgeliefert.fields : [];
        const stelle = (typ) => vomServer.find(f => f?.type === typ);
        // Die Fragen der Typen traegt der Server als eigene Felder nach; er
        // kennzeichnet sie, damit der Formular-Editor sie nicht mitspeichert.
        const typfragen = vomServer.filter(f => f?._verwaltet);

        const liste = [];
        felder.forEach(f => {
            const feld = {
                name: f.name, label: f.label || "(ohne Beschriftung)",
                type: f.type, required: !!f.required,
                options: brauchtOptionen(f.type) ? textZuOptionen(f.optionenText) : f.options,
                showIf: f.showIf || null,
            };
            if (f.type === "costume-type-select") {
                liste.push({ ...feld, options: stelle("costume-type-select")?.options || feld.options });
            } else if (f.type === "section") {
                liste.push({ ...feld, dynamicItems: stelle("section")?.dynamicItems });
            } else if (f.type === "type-questions") {
                liste.push(feld, ...typfragen);
            } else {
                liste.push(feld);
            }
        });
        return liste;
    };

    const setzeFeld = (i, aenderung) =>
        setFelder(vorher => vorher.map((f, idx) => (idx === i ? { ...f, ...aenderung } : f)));

    const verschiebe = (i, richtung) =>
        setFelder(vorher => {
            const liste = [...vorher];
            const ziel = i + richtung;
            if (ziel < 0 || ziel >= liste.length) return vorher;
            [liste[i], liste[ziel]] = [liste[ziel], liste[i]];
            return liste;
        });

    const speichern = async () => {
        setSpeichert(true);
        setMeldung(null);
        try {
            const { data } = await api.put(`/api/costume-forms/${encodeURIComponent(component)}`, {
                scheme: {
                    title: formular?.titel || component,
                    // Das Feld unveraendert zurueckgeben und nur ueberschreiben,
                    // was hier bearbeitbar ist. Sonst gingen Schluessel
                    // verloren, die der Editor gar nicht kennt - etwa die
                    // Bedingung an "zukünftige Häsnummer".
                    fields: felder.map(({ optionenText, ...f }) => ({
                        ...f,
                        name: f.name || technischerName(f.label),
                        ...(brauchtOptionen(f.type) ? { options: textZuOptionen(optionenText) } : {}),
                    })),
                },
            }, kopf());
            // Erst neu laden, dann melden: lade() raeumt die Meldung ab, die
            // Bestaetigung waere sonst sofort wieder verschwunden.
            if (await lade()) {
                setMeldung({
                    art: "success",
                    text: data.geaendert ? "Gespeichert." : "Keine Änderung — nichts zu speichern.",
                });
            }
        } catch (err) {
            setMeldung({ art: "danger", text: err.response?.data?.message || "Speichern fehlgeschlagen." });
        } finally {
            setSpeichert(false);
        }
    };

    return (
        <div>
            {meldung && <div className={`alert alert-${meldung.art} py-2`}>{meldung.text}</div>}

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <span className="text-secondary small">
                    Diese Felder erscheinen bei jedem {cfg.costumeTerm}typ. Was nur für einen
                    bestimmten Typ gilt, steht weiter unten an der Stelle, an der es
                    im Formular auftaucht.
                </span>
                <button className="btn btn-sm btn-outline-info" onClick={() => setZeigtVorschau(true)}>
                    Vorschau
                    {nichtGespeichert && (
                        <span className="badge bg-warning text-dark ms-2">ungespeichert</span>
                    )}
                </button>
            </div>

            <AdminFormVorschau
                offen={zeigtVorschau}
                onSchliessen={() => setZeigtVorschau(false)}
                nichtGespeichert={nichtGespeichert}
                felder={vorschauFelder()}
                titel={`${formular?.bezeichnung || ""} — Vorschau`} />

            {felder.length === 0 && <p className="text-secondary small">Keine Felder.</p>}

            {felder.map((f, i) => (
                <div className={`card mb-2 ${f.festgelegt ? "border-info" : ""}`} key={i}>
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-12 col-md-4">
                                <label className="form-label small mb-1">
                                    Beschriftung
                                    {f.geschuetzt && (
                                        <span className="badge bg-secondary ms-1">vom System benötigt</span>
                                    )}
                                </label>
                                <input className="form-control form-control-sm"
                                    value={f.label || ""} disabled={f.festgelegt}
                                    onChange={e => setzeFeld(i, {
                                        label: e.target.value,
                                        // Bestehende Felder behalten ihren technischen Namen -
                                        // er steckt in gespeicherten Antraegen und Bestellungen.
                                        name: f.name || technischerName(e.target.value),
                                    })} />
                            </div>
                            <div className="col-7 col-md-3">
                                <label className="form-label small mb-1">Feldtyp</label>
                                {f.festgelegt ? (
                                    // Als gesperrtes Eingabefeld, nicht als eingefaerbtes
                                    // Kaestchen: im dunklen Bereich der Administration
                                    // verschluckt bg-body-secondary den Text.
                                    <input className="form-control form-control-sm"
                                        value={typBezeichnung(f.type)} disabled readOnly />
                                ) : (
                                    <select className="form-select form-select-sm" value={f.type}
                                        onChange={e => setzeFeld(i, { type: e.target.value })}>
                                        {FELDTYPEN.map(t => (
                                            <option key={t.wert} value={t.wert}>{t.text}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div className="col-5 col-md-2">
                                {!f.festgelegt && (
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox"
                                            id={`pflicht-allgemein-${i}`}
                                            checked={!!f.required}
                                            onChange={e => setzeFeld(i, { required: e.target.checked })} />
                                        <label className="form-check-label small"
                                            htmlFor={`pflicht-allgemein-${i}`}>Pflicht</label>
                                    </div>
                                )}
                            </div>
                            <div className="col-12 col-md-3 text-md-end">
                                <button className="btn btn-sm btn-outline-secondary me-1" disabled={i === 0}
                                    onClick={() => verschiebe(i, -1)} title="nach oben">↑</button>
                                <button className="btn btn-sm btn-outline-secondary me-1"
                                    disabled={i === felder.length - 1}
                                    onClick={() => verschiebe(i, 1)} title="nach unten">↓</button>
                                <button className="btn btn-sm btn-outline-danger"
                                    disabled={f.geschuetzt || f.festgelegt}
                                    onClick={() => setFelder(felder.filter((_, idx) => idx !== i))}>
                                    Entfernen
                                </button>
                            </div>
                        </div>

                        {!f.festgelegt && brauchtOptionen(f.type) && (
                            <div className="mt-2">
                                <label className="form-label small mb-1">
                                    Auswahlmöglichkeiten — eine je Zeile, wahlweise „Anzeige = gespeicherter Wert"
                                </label>
                                <textarea className="form-control form-control-sm" rows={3}
                                    value={f.optionenText || ""}
                                    onChange={e => setzeFeld(i, { optionenText: e.target.value })} />
                            </div>
                        )}

                        {istTypStelle(f) ? (
                            <div className="mt-2">
                                <div className="form-text mb-2">
                                    {f.type === "section"
                                        ? `An dieser Stelle stehen die Teile, die sich je ${cfg.costumeTerm}typ bestellen lassen.`
                                        : `An dieser Stelle stehen die Felder, die nur für einen bestimmten ${cfg.costumeTerm}typ gelten.`}
                                    {" "}Wähle den Typ, um sie hier zu bearbeiten — mit Bedingungen,
                                    Unterfeldern und allem, was dazugehört.
                                </div>
                                <div className="row g-2 align-items-center">
                                    <div className="col-auto">
                                        <label className="form-label small mb-0" htmlFor={`typwahl-${i}`}>
                                            Felder für Typ
                                        </label>
                                    </div>
                                    <div className="col-12 col-sm-auto">
                                        <select className="form-select form-select-sm" id={`typwahl-${i}`}
                                            value={gewaehlterTyp}
                                            onChange={e => setGewaehlterTyp(e.target.value)}>
                                            <option value="">— {cfg.costumeTerm}typ wählen —</option>
                                            {(typen || []).map(t => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {gewaehlterTyp && (
                                    <div className="mt-3 border-top pt-3">
                                        <AdminCostumeTypeDefinition
                                            typId={Number(gewaehlterTyp)}
                                            typName={(typen || []).find(t => String(t.id) === gewaehlterTyp)?.name || ""}
                                            nurBereich={f.type === "section" ? "ersatzteil" : "antrag"}
                                            sofortOffen
                                        />
                                    </div>
                                )}
                            </div>
                        ) : f.festgelegt ? (
                            <div className="form-text mt-1">
                                {`Die Auswahl kommt aus der Liste der ${cfg.costumeTerm}typen.`}
                            </div>
                        ) : (
                            <div className="form-text mt-1">Technisch: <code>{f.name || "—"}</code></div>
                        )}
                    </div>
                </div>
            ))}

            <button className="btn btn-sm btn-outline-primary"
                onClick={() => setFelder([...felder, {
                    name: "", label: "", type: "text", required: false, optionenText: "",
                }])}>
                + Feld hinzufügen
            </button>

            <div className="mt-3 d-flex gap-2 flex-wrap">
                <button className="btn btn-warning" onClick={speichern} disabled={speichert}>
                    {speichert ? "Speichern…" : "Speichern"}
                </button>
                <button className="btn btn-outline-secondary" onClick={lade} disabled={speichert}>
                    Änderungen verwerfen
                </button>
            </div>
        </div>
    );
};

export default AdminCostumeForms;
